import { usePathname } from 'next/navigation'
import React, { PropsWithChildren, useEffect, useState } from 'react'

import {
  useAuthContext,
  useCurrentUser,
  useIdentifyUserInMonitors,
} from '@/hooks'
import {
  isRootRoute,
  isPublicRoute,
  isProtectedRoute,
  isLandingRoute,
} from '@/utils/auth'

import { Loader } from './Loader'
import PublicLayout from './Public'
import PrivateLayout from './Private'
import { OnboardingLayout } from './Onboarding'

export const RootLayout = ({ children }: PropsWithChildren) => {
  const { session, isLoading } = useAuthContext()

  const currentPath = usePathname()

  const { isFirstLoad: isDocsumUserFirstLoad, currentUser } = useCurrentUser({
    shouldFetch: currentPath !== '/login',
  })

  useIdentifyUserInMonitors({
    user: currentUser,
    isLoading: isDocsumUserFirstLoad,
  })

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const isRootLoading = isLoading && isRootRoute(currentPath)

  if (!mounted || isRootLoading) {
    return (
      <PublicLayout>
        <Loader />
      </PublicLayout>
    )
  }

  const isNotPrivateRoute =
    isRootRoute(currentPath) ||
    isPublicRoute(currentPath) ||
    isProtectedRoute(currentPath) ||
    isLandingRoute(currentPath)

  if (isNotPrivateRoute) {
    const isOnboarding = currentPath.startsWith('/onboarding')
    if (isOnboarding) {
      return <OnboardingLayout>{children}</OnboardingLayout>
    }

    return <PublicLayout>{children}</PublicLayout>
  }

  const isSignedOut = !session
  if (isSignedOut || isDocsumUserFirstLoad) {
    return (
      <PublicLayout>
        <Loader />
      </PublicLayout>
    )
  }

  return <PrivateLayout>{children}</PrivateLayout>
}

RootLayout.displayName = 'RootLayout'
